/**
 * Dynamically loads tracking scripts only when the user has granted consent.
 * Prevents PostHog, Google Analytics, and Customer.io from loading until consent.
 * Modular: add new scripts via the registry pattern.
 */

import type { CookieConsent } from './types';
import { initNewRelicBrowser } from '../telemetry/newRelicBrowser';
import { runtimeEnv } from '@/lib/runtimeEnv';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: any[]) => void;
  }
}

const GA_SCRIPT_ID = 'lureo-ga-gtag';
const GA_CONFIG_SCRIPT_ID = 'lureo-ga-config';

function getGaMeasurementId(): string | null {
  const id = runtimeEnv.VITE_GA4_MEASUREMENT_ID ?? (typeof process !== 'undefined' && process.env?.VITE_GA4_MEASUREMENT_ID);
  return typeof id === 'string' && id ? id : null;
}

/**
 * Load Google Analytics (gtag.js) when analytics consent is granted.
 * Removes the script and clears gtag when consent is revoked.
 */
export function applyGaConsent(consent: CookieConsent): void {
  const measurementId = getGaMeasurementId();
  const enabled = consent.analytics && !!measurementId;

  if (typeof document === 'undefined') return;

  const existingScript = document.getElementById(GA_SCRIPT_ID);
  const existingConfig = document.getElementById(GA_CONFIG_SCRIPT_ID);

  if (enabled) {
    if (existingScript && existingConfig) return; // already loaded

    const configEl = document.createElement('script');
    configEl.id = GA_CONFIG_SCRIPT_ID;
    configEl.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${measurementId}',{send_page_view:false});`;
    document.head.appendChild(configEl);

    const script = document.createElement('script');
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  } else {
    if (existingScript) existingScript.remove();
    if (existingConfig) existingConfig.remove();
    window.gtag = undefined;
    window.dataLayer = [];
  }
}

/**
 * Customer.io: optional separate script (e.g. for messaging).
 * When marketing consent is granted, we load a script if URL is configured.
 * Add VITE_CUSTOMER_IO_SCRIPT_URL to env if you use a separate Customer.io script.
 */
const CUSTOMER_IO_SCRIPT_ID = 'lureo-customer-io';

function getCustomerIoScriptUrl(): string | null {
  const url = runtimeEnv.VITE_CUSTOMER_IO_SCRIPT_URL ?? (typeof process !== 'undefined' && process.env?.VITE_CUSTOMER_IO_SCRIPT_URL);
  return typeof url === 'string' && url ? url : null;
}

export function applyCustomerIoConsent(consent: CookieConsent): void {
  const scriptUrl = getCustomerIoScriptUrl();
  const enabled = consent.marketing && !!scriptUrl;

  if (typeof document === 'undefined') return;

  const existing = document.getElementById(CUSTOMER_IO_SCRIPT_ID);
  if (enabled && !existing && scriptUrl) {
    const script = document.createElement('script');
    script.id = CUSTOMER_IO_SCRIPT_ID;
    script.async = true;
    script.src = scriptUrl;
    document.head.appendChild(script);
  } else if (!enabled && existing) {
    existing.remove();
  }
}

/**
 * Apply all script consent: GA, Customer.io.
 * PostHog is controlled by React (conditional PostHogProvider), not by script injection.
 */
export function applyConsentToScripts(consent: CookieConsent): void {
  applyGaConsent(consent);
  applyCustomerIoConsent(consent);
  if (consent.analytics) initNewRelicBrowser();
}
